"use client";

import { useEffect, useState } from "react";
import {
    Heading,
    ProductCardwithVariants,
    Tabs,
    type ProductColorOption,
    type ProductCardwithVariantsSlotProps,
} from "@farm-junction/design-library";

export type CompareColourColorOption = ProductColorOption & {
    imageSrc?: string;
};

export type CompareColourCard = {
    id: string;
    title: string;
    slug: string;
    imageSrc: string;
    priceRangeText: string;
    colors: CompareColourColorOption[];
};

type CompareColoursWithCompetitorsProps = {
    modelName: string;
    currentModel: CompareColourCard;
    alternatives: CompareColourCard[];
};

const CARD_SLOT_PROPS: ProductCardwithVariantsSlotProps = {
    dividerProps: {
        className: "border-t border-border",
    },
    imageFrameProps: {
        className: "aspect-[435/240] bg-gray-100",
    },
    imageLinkProps: {
        className: "font-normal",
    },
    imgProps: {
        fetchPriority: "low",
        loading: "lazy",
        height: 240,
        width: 435,
    },
    detailsRowProps: {
        className: "!flex-row items-center justify-between gap-1",
    },
    bodyProps: {
        className: "gap-2 p-2.5 md:gap-3.5 md:p-6",
    },
    priceProps: {
        className: "font-sans text-[11px] font-normal leading-none tracking-normal text-on-surface md:text-sm md:whitespace-nowrap",
        level: "p",
        variant: "p",
    },
    titleLinkProps: {
        className: "font-normal text-on-surface hover:text-on-surface",
    },
    titleProps: {
        className: "font-sans text-xs font-semibold leading-none tracking-normal text-on-surface md:text-base",
        level: 3,
        variant: "p",
    },
    variantOptionsProps: {
        slotProps: {
            labelProps: {
                className: "text-[10px] font-semibold uppercase tracking-wide text-on-surface/60 md:text-xs",
            },
            listProps: {
                className: "gap-1.5",
            },
            moreColorsButtonProps: {
                className:
                    "border-border bg-gray-100 px-1 text-[10px] font-medium text-brand-800 hover:bg-gray-100 hover:text-brand-800 hover:opacity-80",
            },
            moreColorsProps: {
                className:
                    "border-border bg-gray-100 px-1 text-[10px] font-medium text-brand-800 hover:bg-gray-100 hover:text-brand-800 hover:opacity-80 hover:no-underline",
            },
            swatchButtonProps: {
                className: "bg-transparent text-on-surface border-transparent hover:bg-transparent hover:opacity-80",
            },
            swatchLinkProps: {
                className:
                    "bg-transparent text-on-surface border-transparent hover:bg-transparent hover:opacity-80 hover:no-underline",
            },
            swatchProps: {
                className: "border border-solid border-border",
            },
        },
    },
};

const colourOptionsLabel = (count: number) =>
    `${count} COLOUR OPTION${count === 1 ? "" : "S"}`;

function ComparisonCard({
    card,
    emphasized = false,
}: {
    card: CompareColourCard;
    emphasized?: boolean;
}) {
    const [activeColorId, setActiveColorId] = useState("");
    const [showAllColors, setShowAllColors] = useState(false);

    useEffect(() => {
        setActiveColorId("");
        setShowAllColors(false);
    }, [card.id]);

    const activeColor = card.colors.find((color) => color.id === activeColorId);
    const displayImage = activeColor?.imageSrc ?? card.colors[0]?.imageSrc ?? card.imageSrc;
    const imageLabel = activeColor?.label
        ? `${card.title} in ${activeColor.label}`
        : card.title;

    return (
        <ProductCardwithVariants
            className={`w-full ${emphasized ? "!bg-primary/5 !border-[#008C8B]" : "!border-border !border-[#EAECEF] !bg-surface"}`}
            colorOptionsLabel={colourOptionsLabel(card.colors.length)}
            colors={card.colors}
            imageSrc={displayImage}
            onColorClick={(color) => setActiveColorId(color.id)}
            onMoreColorsClick={() => setShowAllColors(true)}
            priceRangeText={card.priceRangeText}
            slotProps={{
                ...CARD_SLOT_PROPS,
                imgProps: {
                    ...CARD_SLOT_PROPS.imgProps,
                    alt: imageLabel,
                    title: imageLabel,
                    fetchPriority: emphasized ? "high" : "low",
                },
            }}
            slug={card.slug}
            title={card.title}
            visibleColorCount={showAllColors ? card.colors.length : 4}
        />
    );
}

export default function CompareColoursWithCompetitors({
    modelName,
    currentModel,
    alternatives,
}: CompareColoursWithCompetitorsProps) {
    const [activeTab, setActiveTab] = useState(0);

    if (!alternatives.length) return null;

    const selectedAlternative = alternatives[Math.min(activeTab, alternatives.length - 1)];

    return (
        <section className="mb-4 md:mb-6" data-testid="colours-compare-competitors">
            <div className="mb-5">
                <Heading
                    variant="h2"
                    level={2}
                    className="!mb-1 text-[20px] font-semibold leading-6 text-on-surface md:font-bold"
                >
                    {`Compare ${modelName} Colours With Competitors`}
                </Heading>
                <p className="mt-1 text-sm text-on-surface/60">
                    {`Compare The ${modelName}'s Colour Range Against A Rival.`}
                </p>
            </div>

            <Tabs
                className="mb-4 border-b border-border md:mb-5"
                activeTab={activeTab}
                onTabChange={setActiveTab}
                slotProps={{
                    activeButtonProps: {
                        className: "text-[#008C8B] border-[#008C8B] bg-transparent hover:bg-transparent hover:text-[#008C8B]",
                    },
                    buttonProps: {
                        variant: "ghost",
                        className:
                            "rounded-none bg-transparent px-3 py-2 text-sm text-brand-800 border-b-2 border-transparent hover:bg-transparent hover:text-[#008C8B] hover:border-[#008C8B] md:px-4 md:py-2.5 md:text-base",
                    },
                }}
                tabs={alternatives.map((alternative) => ({ title: alternative.title }))}
            />

            <div className="grid grid-cols-2 gap-2 md:gap-5">
                <ComparisonCard card={currentModel} emphasized />
                <ComparisonCard key={selectedAlternative.id} card={selectedAlternative} />
            </div>
        </section>
    );
}
